// --- Helper: Parse lng,lat string ---
function parseLngLat(value: string): [number, number] | null {
  // Strict regex for lng,lat coordinates
  const match = value.trim().match(/^(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)/);
  if (!match) return null;
  const lng = parseFloat(match[1]);
  const lat = parseFloat(match[2]);
  if (isNaN(lng) || isNaN(lat)) return null;
  return [lng, lat];
}

// --- Helper: Calculate polygon center from coordinates ---
function getPolygonCenter(coordinates: [number, number][]): [number, number] {
  if (!coordinates || coordinates.length === 0) {
    return [0, 0];
  }
  let lngSum = 0, latSum = 0;
  coordinates.forEach(([lng, lat]) => {
    lngSum += lng;
    latSum += lat;
  });
  return [lngSum / coordinates.length, latSum / coordinates.length];
}
import { CSSProperties, FC, useEffect, useRef } from "react";
import mapboxgl, { LngLatLike } from "mapbox-gl";
import React from "react";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import { ExtendedMapEventType, IMapBoundaryProps } from "./model";
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import "mapbox-gl/dist/mapbox-gl.css";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";
import "./style.css";
import {
  defaultPanelStyle,
  defaultStyle,
  panelPositions,
  positioningStyle,
} from "./panelStyles";
import {
  CircleMode,
  DragCircleMode,
  DirectMode,
  SimpleSelectMode,
} from "maplibre-gl-draw-circle";
import { area } from "@turf/area";
import { centroid } from "@turf/centroid";
import { hexToRGB } from "../utils";

const Map: FC<IMapBoundaryProps> = ({
  accessToken,
  zoomLevel,
  style,
  onSave,
  onGetCenter,
  onGetBoundaries,
  drawRef,
  saveBtnRef,
  deleteBtnRef,
  enableCustomPanel,
  enableSearch,
  panelPosition,
  searchbarPosition,
  panelStyle,
  boundaryColour,
  mapStyle,
  children,
  onPolygonDrawn,
}) => {
  const mapContainerRef = useRef<HTMLDivElement>();

  const mapRef = useRef<mapboxgl.Map | null>(null);

  const [mapboxToken, setMapboxToken] = React.useState<string>("");
  const [localStyle, setLocalStyle] =
    React.useState<CSSProperties>(defaultStyle);
  const [localPanelStyle, setLocalPanelStyle] =
    React.useState<CSSProperties>(defaultPanelStyle);
  const [localZoomLevel, setLocalZoomLevel] = React.useState<number>(15);
  const [localSearchBarPosition, setLocalSearchbarPosition] =
    React.useState<string>("top-left");
  const [localPanelPosition, setLocalPanelPosition] =
    React.useState<string>("top-left");
  const [localBoundaryColour, setLocalBoundaryColour] =
    React.useState<string>("#d5e0e2");
  const [boundedGeography, setBoundedGeography] = React.useState<any>([]);
  const [localCenter, setLocalCenter] = React.useState<LngLatLike | undefined>([
    28.191795, -25.854708,
  ]);
  const [containerRef, setContainerRef] =
    React.useState<React.MutableRefObject<HTMLDivElement | null>>();
  const [isPanelEnabled, setIsPanelEnabled] = React.useState<boolean>(false);
  const [isSearchActive, setIsSearchActive] = React.useState<boolean>(false);

  // Set default style to satellite if not provided, and check for persisted style
  const defaultMapStyle = "mapbox://styles/mapbox/satellite-v9";
  const getInitialMapStyle = () => {
  // Only use mapStyle prop if no persisted value
  if (typeof window !== 'undefined') {
    const saved = localStorage.getItem("mapbox-style");
    if (saved) return saved;
  }
  return mapStyle || defaultMapStyle;
  };
  const [localMapStyle, setLocalMapStyle] = React.useState<string>(getInitialMapStyle());

  const geoCoderRef = useRef<MapboxGeocoder | null>(null);
  const geocoderInputRef = useRef<HTMLInputElement | null>(null);
  const geocoderListenersRef = useRef<any>({
    handleFocus: null,
    handleBlur: null,
    handleResults: null,
    handleResult: null,
    handleClear: null,
    timeoutId: null,
    blurTimeoutId: null,
  });

    // Expose setBoundedGeography and onGetBoundaries for configurable actions
    React.useEffect(() => {
      (window as any).redrawBoundaries = (boundaries: any) => {
        setBoundedGeography(boundaries);
      };
      (window as any).getBoundariesAsync = async () => {
        if (typeof onGetBoundaries === 'function') {
          return await onGetBoundaries();
        }
        return [];
      };
      return () => {
        delete (window as any).redrawBoundaries;
        delete (window as any).getBoundariesAsync;
      };
    }, [onGetBoundaries]);

    // Fly to new center when localCenter changes, only if map is loaded
    useEffect(() => {
      if (mapRef.current && localCenter) {
        if (mapRef.current.isStyleLoaded && mapRef.current.isStyleLoaded()) {
          mapRef.current.flyTo({ center: localCenter });
        } else {
          const onLoad = () => {
            mapRef.current?.flyTo({ center: localCenter });
            mapRef.current?.off('load', onLoad);
          };
          mapRef.current.on('load', onLoad);
        }
      }
    }, [localCenter]);

  // Enrich polygon with area and centroid
  function enrichPolygonWithCenterAndArea(feature: any): void {
    if (feature.geometry?.type === "Polygon") {
      feature.properties = { ...feature.properties, area: area(feature) };
      try {
        const c = centroid(feature as any);
        if (c && c.geometry && Array.isArray(c.geometry.coordinates)) {
          const [lng, lat] = c.geometry.coordinates;
          feature.properties.center = [lng, lat];           // [lng, lat] array
          feature.properties.centerLng = lng;               // separate lng
          feature.properties.centerLat = lat;               // separate lat
        }
      } catch (err) {
        // ignore centroid errors
      }
    }
  }

  function updateArea(e: any) {
    // Called when a polygon is finished
    if (e && e.features && e.features[0]) {
      const feature = e.features[0];
      if (!feature.properties?.isCircle && typeof onPolygonDrawn === 'function') {
        // For circles, the modal is deferred to draw.update (after the user interacts)
        enrichPolygonWithCenterAndArea(feature);
        onPolygonDrawn(feature);
      }
    }
  }

  function handleDrawUpdate(e: any) {
    
    if (e && e.features && e.features[0]) {
       enrichPolygonWithCenterAndArea(e.features[0]);
      const feature = e.features[0];
      if (feature.properties?.isCircle && typeof onPolygonDrawn === 'function') {
        // Circle moved or expanded — open modal
        onPolygonDrawn(feature);
      } else {
        // Polygon points adjusted — silently update area without opening modal
        enrichPolygonWithCenterAndArea(feature);
      }
    }
  }

  function deleteItem(e: any) {
    if (e && e.features && e.features[0]) {
      const featureToDelete = e.features[0];
      drawRef.current?.delete(featureToDelete.id);
    }
  }

  function handleDeleteBtnClick(e: any) {
    if (e && e.preventDefault) e.preventDefault();
    const selectedFeatures = drawRef.current?.getSelected();
    if (selectedFeatures && selectedFeatures.features.length > 0) {
      const featureToDelete = selectedFeatures.features[0].id.toString();
      drawRef.current.delete(featureToDelete);
      if (mapRef.current) {
        const map = mapRef.current;
        const hasFill = !!map.getLayer(featureToDelete);
        const hasLine = !!map.getLayer(`line-${featureToDelete}`);
        const hasSource = !!map.getSource(featureToDelete);
        if (hasFill) map.removeLayer(featureToDelete);
        if (hasLine) map.removeLayer(`line-${featureToDelete}`);
        if (hasSource) map.removeSource(featureToDelete);
      }
    }
  }

  async function handleRefBtnClick(e?: any) {
    if (e && e.preventDefault) e.preventDefault();
    if (saveBtnRef && saveBtnRef.current && onSave) {
      const featureCollection = drawRef.current?.getAll();
      if (featureCollection && featureCollection.features) {
        // Enrich all features with area and center before saving
        featureCollection.features.forEach(enrichPolygonWithCenterAndArea);
        try {
          await onSave(featureCollection.features);
        } catch (error) {
          console.error("Save failed:", error);
          throw error;
        }
      }
    }
  }

  useEffect(() => {
    setContainerRef(mapContainerRef);
  }, [mapContainerRef, localMapStyle]);

  useEffect(() => {
    // TODO: Add the mapbox token to the application settings
    mapboxgl.accessToken = mapboxToken;

    if (containerRef?.current) {
      if (!mapRef?.current) {
        mapRef.current = new mapboxgl.Map({
          container: mapContainerRef.current,
          style: localMapStyle,
          center: localCenter,
          zoom: localZoomLevel,
        });
      }
    }

    // allow boundary re-drawing and fly to selected polygon center
    const handleSelectionChange = (e: any) => {
      if (e && e.features && e.features.length > 0) {
        const selectedPolygon = e.features[0];
        if (selectedPolygon.geometry.type === "Polygon") {
          try {
            const c = centroid(selectedPolygon as any);
            if (c && c.geometry && Array.isArray(c.geometry.coordinates)) {
              const flyCenter = c.geometry.coordinates as [number, number];
              if (mapRef.current && mapRef.current.isStyleLoaded && mapRef.current.isStyleLoaded()) {
                mapRef.current.flyTo({ center: flyCenter });
              } else if (mapRef.current) {
                const onLoad = () => {
                  mapRef.current?.flyTo({ center: flyCenter });
                  mapRef.current?.off('load', onLoad);
                };
                mapRef.current.on('load', onLoad);
              }
            }
          } catch (err) {
            // fallback to average
            const coordinates = selectedPolygon.geometry.coordinates[0];
            const center = getPolygonCenter(coordinates);
            if (mapRef.current && mapRef.current.isStyleLoaded && mapRef.current.isStyleLoaded()) {
              mapRef.current.flyTo({ center });
            } else if (mapRef.current) {
              const onLoad = () => {
                mapRef.current?.flyTo({ center });
                mapRef.current?.off('load', onLoad);
              };
              mapRef.current.on('load', onLoad);
            }
          }
        }
        if (drawRef.current && selectedPolygon?.id != null) {
          drawRef.current.changeMode("direct_select", {
            featureId: String(selectedPolygon.id),
          });
        }
      }
    };

    if (mapRef?.current) {
      if (!drawRef.current) {
        drawRef.current = new MapboxDraw({
          displayControlsDefault: false,
          userProperties: true,
          controls: {
            polygon: false,
            trash: false,
          },
          defaultMode: "simple_select",
          modes: {
            ...MapboxDraw.modes,
            draw_circle: CircleMode,
            drag_circle: DragCircleMode,
            direct_select: DirectMode,
            simple_select: SimpleSelectMode,
          },
        });
      }

      if (!mapRef.current.hasControl(drawRef.current)) {
        mapRef.current.addControl(drawRef.current);
      }

      if (boundedGeography && boundedGeography.length > 0) {
        mapRef.current.on("load", () => {
          const colorWithoutAlpha =
            localBoundaryColour.length === 9
              ? hexToRGB(localBoundaryColour)
              : localBoundaryColour;

          boundedGeography.forEach((geo: any) => {
            const layerId = geo.id;
            if (
              !mapRef.current.getStyle().layers.some((l) => l.id === layerId) &&
              !mapRef.current
                .getStyle()
                .layers.some((l) => l.id === `line-${layerId}`)
            ) {
              mapRef.current?.addSource(layerId, {
                type: "geojson",
                data: geo,
              });
              mapRef.current.addLayer({
                id: layerId,
                type: "fill",
                source: layerId,
                layout: {},
                paint: {
                  // Use feature color property if present, else fallback
                  "fill-color": ["coalesce", ["get", "color"], colorWithoutAlpha],
                  "fill-opacity": 0.5,
                },
              });
              mapRef.current.addLayer({
                id: `line-${layerId}`,
                type: "line",
                source: layerId,
                layout: {},
                paint: {
                  "line-color": "#000",
                  "line-width": 1,
                },
              });
              drawRef.current?.add(geo);
            }
          });

          // Fly to the first polygon's centroid if available
          const selectedPolygon = boundedGeography[0];
          if (selectedPolygon && selectedPolygon.geometry && selectedPolygon.geometry.type === "Polygon") {
            try {
              const c = centroid(selectedPolygon as any);
              if (c && c.geometry && Array.isArray(c.geometry.coordinates)) {
                mapRef.current.flyTo({ center: c.geometry.coordinates as [number, number] });
              }
            } catch (err) {
              // fallback to simple average if centroid fails
              const coordinates = selectedPolygon.geometry.coordinates[0];
              const center = getPolygonCenter(coordinates);
              mapRef.current.flyTo({ center });
            }
          }
        });
      }

      if (enableSearch) {
        if (!geoCoderRef.current) {
          geoCoderRef.current = new MapboxGeocoder({
            accessToken: mapboxgl.accessToken,
            mapboxgl: mapboxgl as unknown as typeof import("mapbox-gl"),
            // Add localGeocoder for coordinate search
            localGeocoder: (query: string) => {
              const coord = parseLngLat(query);
              if (coord) {
                return [{
                  center: coord,
                  geometry: { type: "Point", coordinates: coord },
                  place_name: `LngLat: ${coord[0]}, ${coord[1]}`,
                  place_type: ["coordinate"],
                  type: "Feature",
                  properties: {},
                  bbox: undefined,
                  relevance: 1,
                  text: `LngLat: ${coord[0]}, ${coord[1]}`,
                  address: undefined,
                  context: undefined,
                }];
              }
              return null;
            },
          });
        }

        if (mapRef.current) {
          const geocoder = geoCoderRef.current as MapboxGeocoder;
          if (!mapRef.current.hasControl(geocoder)) {
            mapRef.current.addControl(geocoder, localSearchBarPosition as any);
            
            // Store reference for cleanup
            const handleFocus = () => {
              // Clear any pending blur timeout when focusing
              if (geocoderListenersRef.current.blurTimeoutId) {
                clearTimeout(geocoderListenersRef.current.blurTimeoutId);
                geocoderListenersRef.current.blurTimeoutId = null;
              }
              setIsSearchActive(true);
            };

            const handleBlur = () => {
              // Don't show panel immediately - wait for either result selection or clear
              // This allows users to interact with the dropdown without the panel reappearing
            };

            // Handler for when results are shown - keep panel hidden
            const handleResults = () => {
              if (geocoderListenersRef.current.blurTimeoutId) {
                clearTimeout(geocoderListenersRef.current.blurTimeoutId);
                geocoderListenersRef.current.blurTimeoutId = null;
              }
              setIsSearchActive(true);
            };

            // Handler for when a result is selected - show panel again
            const handleResult = () => {
              if (geocoderListenersRef.current.blurTimeoutId) {
                clearTimeout(geocoderListenersRef.current.blurTimeoutId);
                geocoderListenersRef.current.blurTimeoutId = null;
              }
              setIsSearchActive(false);
            };

            // Handler for when search is cleared - show panel again
            const handleClear = () => {
              if (geocoderListenersRef.current.blurTimeoutId) {
                clearTimeout(geocoderListenersRef.current.blurTimeoutId);
                geocoderListenersRef.current.blurTimeoutId = null;
              }
              setIsSearchActive(false);
            };

            // Store handlers and timeout for cleanup
            geocoderListenersRef.current.handleFocus = handleFocus;
            geocoderListenersRef.current.handleBlur = handleBlur;
            geocoderListenersRef.current.handleResults = handleResults;
            geocoderListenersRef.current.handleResult = handleResult;
            geocoderListenersRef.current.handleClear = handleClear;

            // Listen for geocoder events
            geocoder.on('results', handleResults);
            geocoder.on('result', handleResult);
            geocoder.on('clear', handleClear);

            // Add focus/blur listeners to track search activity
            const timeoutId = setTimeout(() => {
              const geocoderInput = document.querySelector('.mapboxgl-ctrl-geocoder--input') as HTMLInputElement;
              geocoderInputRef.current = geocoderInput;
              if (geocoderInput) {
                geocoderInput.addEventListener('focus', handleFocus);
                geocoderInput.addEventListener('blur', handleBlur);
              }
            }, 100);

            geocoderListenersRef.current.timeoutId = timeoutId;
          }
        }
      } else {
        if (geoCoderRef.current) {
          if (mapRef.current) {
            try {
              const geocoder = geoCoderRef.current as MapboxGeocoder;

              if (geocoder && mapRef.current.hasControl(geocoder)) {
                mapRef.current.removeControl(geocoder);
              }
            } catch (e) {
              console.log("LOG::Error removing geocoder: ", e.message);
            }
          }
        }
      }

      const extendedMap = mapRef.current as mapboxgl.Map & {
        on<T extends keyof ExtendedMapEventType>(
          type: T,
          listener: (event: ExtendedMapEventType[T]) => void
        ): mapboxgl.Map;
      };

      extendedMap.on("draw.create", updateArea);
      extendedMap.on("draw.update", handleDrawUpdate);
      extendedMap.on("draw.delete", deleteItem);
      mapRef.current.on("draw.selectionchange", handleSelectionChange);
    }

    // Cleanup function to remove event listeners
    return () => {
      // Remove draw event listeners to prevent stacking on re-run
      if (mapRef.current) {
        (mapRef.current as any).off("draw.create", updateArea);
        (mapRef.current as any).off("draw.delete", deleteItem);
        (mapRef.current as any).off("draw.update", handleDrawUpdate);
        (mapRef.current as any).off("draw.selectionchange", handleSelectionChange);
      }

      const { handleFocus, handleBlur, handleResults, handleResult, handleClear, timeoutId, blurTimeoutId } = geocoderListenersRef.current;

      // Clear the timeout if it's still pending
      if (timeoutId) {
        clearTimeout(timeoutId);
        geocoderListenersRef.current.timeoutId = null;
      }

      // Clear the blur timeout if it's still pending
      if (blurTimeoutId) {
        clearTimeout(blurTimeoutId);
        geocoderListenersRef.current.blurTimeoutId = null;
      }

      // Remove event listeners from geocoder input
      if (geocoderInputRef.current && handleFocus && handleBlur) {
        geocoderInputRef.current.removeEventListener('focus', handleFocus);
        geocoderInputRef.current.removeEventListener('blur', handleBlur);
      }

      // Remove geocoder event listeners
      if (geoCoderRef.current) {
        if (handleResults) geoCoderRef.current.off('results', handleResults);
        if (handleResult) geoCoderRef.current.off('result', handleResult);
        if (handleClear) geoCoderRef.current.off('clear', handleClear);
      }

      // Reset refs
      geocoderListenersRef.current.handleFocus = null;
      geocoderListenersRef.current.handleBlur = null;
      geocoderListenersRef.current.handleResults = null;
      geocoderListenersRef.current.handleResult = null;
      geocoderListenersRef.current.handleClear = null;
    };
  }, [
    accessToken,
    onSave,
    zoomLevel,
    enableSearch,
    boundedGeography,
    searchbarPosition,
    mapRef,
    geoCoderRef,
    containerRef,
    drawRef,
    mapStyle,
    localMapStyle,
  ]);

  useEffect(() => {
    if (accessToken) {
      setMapboxToken(accessToken);
    }

    if (style) {
      setLocalStyle(style);
    }

    if (zoomLevel) {
      setLocalZoomLevel(zoomLevel);
    }

    if (searchbarPosition) {
      setLocalSearchbarPosition(searchbarPosition);
    }

    if (boundaryColour) {
      setLocalBoundaryColour(boundaryColour);
    }

    // Only update localMapStyle if mapStyle prop changes and is different from both current and persisted
    if (mapStyle && typeof window !== 'undefined') {
      const persisted = localStorage.getItem("mapbox-style");
      if (!persisted && localMapStyle !== mapStyle) {
        setLocalMapStyle(mapStyle);
      }
    }

    const panelPositioningStyle = 
      enableCustomPanel &&
      enableSearch &&
      localPanelPosition === searchbarPosition
        ? positioningStyle[panelPosition]
        : panelPositions[panelPosition];

    if (enableCustomPanel) {
      setLocalPanelStyle((prev) => ({
        ...prev,
        ...panelStyle,
        ...panelPositioningStyle,
      }));
    }

    if (panelPosition) {
      setLocalPanelStyle((prev) => ({
        ...prev,
        top: undefined,
        right: undefined,
        bottom: undefined,
        left: undefined,
        ...panelStyle,
        ...panelPositioningStyle,
      }));
    }

    setLocalPanelPosition(panelPosition);

    setIsPanelEnabled(enableCustomPanel);
  }, [
    accessToken,
    setMapboxToken,
    style,
    setLocalStyle,
    zoomLevel,
    setLocalZoomLevel,
    setLocalCenter,
    searchbarPosition,
    setLocalSearchbarPosition,
    enableCustomPanel,
    enableSearch,
    panelPosition,
    panelStyle,
    boundaryColour,
    setLocalBoundaryColour,
    localPanelPosition,
    mapStyle,
    setLocalMapStyle,
    localMapStyle,
  ]);

  useEffect(() => {
    if (onGetCenter) {
      onGetCenter()
        .then((center: LngLatLike) => {
          setLocalCenter(center);
        })
        .catch((err) => {
          console.error("LOG::onGetCenter:Error", err);
        });
    }

    if (onGetBoundaries) {
      onGetBoundaries()
        .then((boundaries) => {
          if (boundaries) {
            setBoundedGeography(boundaries);
          }
        })
        .catch((err) => {
          console.error("LOG::onGetBoundaries:Error", err);
        });
    }
  }, [onGetCenter, setLocalCenter, onGetBoundaries, setBoundedGeography]);

  // Add after map is initialized (inside useEffect where mapRef.current is set)
  useEffect(() => {
  if (!mapRef.current) return () => {};

  // Remove any existing style toggle control
  const existing = document.getElementById('map-style-toggle');
  if (existing) existing.remove();

  // Create style toggle control
  const styleToggle = document.createElement('div');
  styleToggle.id = 'map-style-toggle';
  styleToggle.style.position = 'absolute';
  styleToggle.style.top = '16px';
  styleToggle.style.right = '16px';
  styleToggle.style.zIndex = '1001';
  styleToggle.style.background = '#fff';
  styleToggle.style.border = '1px solid #ccc';
  styleToggle.style.borderRadius = '6px';
  styleToggle.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
  styleToggle.style.display = 'flex';
  styleToggle.style.gap = '0';
  styleToggle.style.overflow = 'hidden';

  const styles = [
      { label: 'Streets', url: 'mapbox://styles/mapbox/streets-v11' },
      { label: 'Satellite', url: 'mapbox://styles/mapbox/satellite-v9' },
      { label: 'Outdoors', url: 'mapbox://styles/mapbox/outdoors-v11' }
    ];

    styles.forEach(({ label, url }) => {
      const btn = document.createElement('button');
      btn.innerText = label;
      btn.style.padding = '8px 16px';
      btn.style.border = 'none';
      btn.style.background = 'none';
      btn.style.cursor = 'pointer';
      btn.style.fontWeight = 'bold';
      btn.style.fontSize = '14px';
      btn.onmouseenter = () => (btn.style.background = '#f0f0f0');
      btn.onmouseleave = () => (btn.style.background = 'none');
      btn.onclick = () => handleStyleChange(url);
      styleToggle.appendChild(btn);
    });

    // Attach to map container
    mapContainerRef.current?.appendChild(styleToggle);

    // Cleanup on unmount
    return () => {
      styleToggle.remove();
    };
  }, [mapRef.current]);



  // Helper to re-add polygons and labels after style change
  const restorePolygonsAndLabels = React.useCallback(() => {
    if (!mapRef.current || !boundedGeography || boundedGeography.length === 0) return;
    const colorWithoutAlpha =
      localBoundaryColour.length === 9
        ? hexToRGB(localBoundaryColour)
        : localBoundaryColour;
    boundedGeography.forEach((geo: any) => {
      const layerId = geo.id;
      if (!mapRef.current.getSource(layerId)) {
        mapRef.current.addSource(layerId, {
          type: "geojson",
          data: geo,
        });
      }
      if (!mapRef.current.getLayer(layerId)) {
        mapRef.current.addLayer({
          id: layerId,
          type: "fill",
          source: layerId,
          layout: {},
          paint: {
            // Use feature color property if present, else fallback
            "fill-color": ["coalesce", ["get", "color"], colorWithoutAlpha],
            "fill-opacity": 0.5,
          },
        });
      }
      if (!mapRef.current.getLayer(`line-${layerId}`)) {
        mapRef.current.addLayer({
          id: `line-${layerId}`,
          type: "line",
          source: layerId,
          layout: {},
          paint: {
            "line-color": "#000",
            "line-width": 1,
          },
        });
      }
      if (!mapRef.current.getLayer(`${layerId}-label`)) {
        mapRef.current.addLayer({
          id: `${layerId}-label`,
          type: "symbol",
          source: layerId,
          layout: {
            "text-field": ["coalesce", ["get", "name"], "Unnamed"],
            "text-size": 16,
            "text-anchor": "center",
            "text-offset": [0, 0.5],
          },
          paint: {
            "text-color": "#333",
            "text-halo-color": "#fff",
            "text-halo-width": 2,
          },
          minzoom: 5,
        }, `line-${layerId}`);
      }
    });
  }, [boundedGeography, localBoundaryColour]);


  const handleStyleChange = (url: string) => {
    setLocalMapStyle(url);
    if (typeof window !== 'undefined') localStorage.setItem("mapbox-style", url);
    if (mapRef.current) {
      mapRef.current.setStyle(url);
      // No need to attach event here, handled by useEffect below
    }
  };

  // Track search bar focus/blur to toggle panel color
  React.useEffect(() => {
    if (!enableSearch) return undefined;

    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (target?.closest('.mapboxgl-ctrl-geocoder')) {
        setIsSearchActive(true);
      }
    };

    const handleFocusOut = (e: FocusEvent) => {
      const related = e.relatedTarget as HTMLElement | null;
      if (!related?.closest('.mapboxgl-ctrl-geocoder')) {
        setIsSearchActive(false);
      }
    };

    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);

    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
    };
  }, [enableSearch]);

  // Always restore polygons/labels after any style change
  React.useEffect(() => {
    if (!mapRef.current) return () => {};
    const map = mapRef.current;
    const onStyle = () => {
      restorePolygonsAndLabels();
    };
    map.on('style.load', onStyle);
    return () => {
      map.off('style.load', onStyle);
    };
  }, [restorePolygonsAndLabels]);

  return (
    <>
      {mapboxToken && (
        <div>
          <div ref={containerRef} style={localStyle} id="map" />

          {isPanelEnabled && (
            <div
              style={{
                ...localPanelStyle,
                opacity: isSearchActive ? 0 : 1,
                pointerEvents: isSearchActive ? 'none' : 'auto',
                transition: 'opacity 0.3s ease',
              }}
              id="panel"
            >
              {children}
              <button
                type="button"
                ref={saveBtnRef}
                style={{ display: "none" }}
                onClick={handleRefBtnClick}
              >
                Save
              </button>
              <button
                type="button"
                ref={deleteBtnRef}
                style={{ display: "none" }}
                onClick={handleDeleteBtnClick}
              >
                Delete
              </button>
            </div>
          )}
        </div>
      )}
      {!mapboxToken && (
        <div>Mapbox Access Token is required to render the map</div>
      )}
    </>
  );
};

export default Map;

